import { useEffect, useState } from "react";

import { useAuth } from "../../../contexts/AuthContext";
import { whatsappService } from "../../../services/whatsapp.service";

import styles from "./LeadWhatsApp.module.css";

export const MEDIA_PENDING_MESSAGE = "El archivo todavía se está descargando de WhatsApp.";
export const MEDIA_FAILED_MESSAGE = "No se pudo bajar el archivo.";

const objectUrlCache = new Map();
const ON_DEMAND_TYPES = new Set(["document", "video"]);

export const clearMediaCache = () => {
  objectUrlCache.forEach((promise) => {
    promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  });
  objectUrlCache.clear();
};

const cacheKeyFor = (userId, messageId) => `${userId ?? "sin-sesion"}:${messageId}`;

const loadObjectUrl = (userId, messageId) => {
  const cacheKey = cacheKeyFor(userId, messageId);
  if (!objectUrlCache.has(cacheKey)) {
    const promise = whatsappService
      .fetchMediaBlob(messageId)
      .then((blob) => URL.createObjectURL(blob))
      .catch((error) => {
        objectUrlCache.delete(cacheKey);
        throw error;
      });
    objectUrlCache.set(cacheKey, promise);
  }
  return objectUrlCache.get(cacheKey);
};

const useAuthenticatedUserId = () => useAuth()?.auth?.user?.id ?? null;

export const useWhatsAppMediaUrl = (message, { enabled = true } = {}) => {
  const available = Boolean(message?.media?.available);
  const messageId = message?.id;
  const userId = useAuthenticatedUserId();
  const [state, setState] = useState({ url: null, error: null, loading: available && enabled });

  useEffect(() => {
    if (!available || !messageId || !enabled) {
      setState({ url: null, error: null, loading: false });
      return undefined;
    }
    let mounted = true;
    setState({ url: null, error: null, loading: true });
    loadObjectUrl(userId, messageId)
      .then((url) => {
        if (mounted) setState({ url, error: null, loading: false });
      })
      .catch(() => {
        if (mounted) setState({ url: null, error: MEDIA_FAILED_MESSAGE, loading: false });
      });
    return () => {
      mounted = false;
    };
  }, [available, messageId, enabled, userId]);

  return state;
};

const download = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "archivo";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const WhatsAppMediaAttachment = ({ message }) => {
  const media = message?.media;
  const type = message?.message_type;
  const onDemand = ON_DEMAND_TYPES.has(type);
  const userId = useAuthenticatedUserId();
  const [openedVideoId, setOpenedVideoId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const videoOpened = type === "video" && openedVideoId != null && openedVideoId === message?.id;
  const { url, error, loading } = useWhatsAppMediaUrl(message, {
    enabled: !onDemand || videoOpened,
  });

  if (!media) return null;

  if (!media.available) {
    return (
      <span className={styles.mediaNote}>{media.error || MEDIA_PENDING_MESSAGE}</span>
    );
  }
  const fileName = media.file_name || "archivo";

  if (type === "document") {
    const handleDownload = async () => {
      setDownloading(true);
      setDownloadError(null);
      try {
        download(await loadObjectUrl(userId, message.id), fileName);
      } catch {
        setDownloadError(MEDIA_FAILED_MESSAGE);
      } finally {
        setDownloading(false);
      }
    };
    return (
      <>
        <button
          type="button"
          className={styles.mediaDocument}
          onClick={handleDownload}
          disabled={downloading}
        >
          <span className={styles.mediaDocumentIcon} aria-hidden="true">
            ⤓
          </span>
          <span className={styles.mediaDocumentName}>{fileName}</span>
        </button>
        {downloadError && <span className={styles.mediaNote}>{downloadError}</span>}
      </>
    );
  }
  if (type === "video" && !videoOpened) {
    return (
      <button type="button" className={styles.mediaDocument} onClick={() => setOpenedVideoId(message.id)}>
        <span className={styles.mediaDocumentIcon} aria-hidden="true">
          ▶
        </span>
        <span className={styles.mediaDocumentName}>{fileName}</span>
      </button>
    );
  }
  if (loading) {
    return <span className={styles.mediaNote}>Cargando archivo...</span>;
  }
  if (error || !url) {
    return <span className={styles.mediaNote}>{error || MEDIA_FAILED_MESSAGE}</span>;
  }

  if (type === "image" || type === "sticker") {
    return (
      <button
        type="button"
        className={styles.mediaImageButton}
        onClick={() => window.open(url, "_blank", "noopener")}
        aria-label={`Abrir ${fileName}`}
      >
        <img className={styles.mediaImage} src={url} alt={fileName} />
      </button>
    );
  }
  if (type === "audio") {
    return <audio className={styles.mediaAudio} controls src={url} aria-label={fileName} />;
  }
  if (type === "video") {
    return <video className={styles.mediaVideo} controls autoPlay src={url} aria-label={fileName} />;
  }
  return (
    <button
      type="button"
      className={styles.mediaDocument}
      onClick={() => download(url, fileName)}
    >
      <span className={styles.mediaDocumentIcon} aria-hidden="true">
        ⤓
      </span>
      <span className={styles.mediaDocumentName}>{fileName}</span>
    </button>
  );
};

export default WhatsAppMediaAttachment;
